<template>
  <div class="page-wrapper">
    <div class="paper-list">
      <div class="hero-banner">
        <div class="hero-bg"></div>
        <div class="hero-content">
          <div class="hero-text">
            <h2 class="page-title">在线试卷</h2>
            <p class="page-desc">完成下列试卷，检验你的学习成果</p>
          </div>
          <div class="hero-actions">
            <button class="btn btn-train" @click="handleTrain">
              <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
              训练模式
            </button>
            <button class="btn btn-primary" :disabled="loading" @click="handleCreate">
              <span v-if="loading" class="spinner"></span>
              <svg v-else class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
              {{ loading ? '生成中...' : '生成试卷' }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="list.length === 0 && !loading" class="empty-state">
        <div class="empty-icon">📝</div>
        <p class="empty-text">还没有试卷，点击上方按钮生成一份吧</p>
      </div>

      <div v-else>
        <div class="paper-grid">
          <div
            v-for="item in list"
            :key="item.id"
            class="paper-card"
            :class="{ submitted: item.status === 1 || item.status === 'submitted' }"
          >
            <div class="card-top">
              <div class="paper-icon">📄</div>
              <div class="paper-info">
                <h3 class="paper-title">{{ item.title || `试卷 #${item.id}` }}</h3>
                <div class="paper-meta">
                  <span v-if="item.paper_no">编号 {{ item.paper_no }}</span>
                  <span v-if="item.total_score !== undefined"> · 满分 {{ item.total_score }} 分</span>
                </div>
              </div>
              <div class="status-tag" :class="getStatusClass(item.status)">
                {{ getStatusText(item.status) }}
              </div>
            </div>

            <div class="card-body">
              <div class="info-row">
                <span class="label">题目数</span>
                <span class="value">{{ item.question_count || '-' }}</span>
              </div>
              <div class="info-row" v-if="item.user_total_score !== undefined && item.user_total_score !== null">
                <span class="label">得分</span>
                <span class="value score" :class="{ pass: isPass(item), fail: !isPass(item) }">
                  {{ item.user_total_score }} 分
                </span>
              </div>
              <div class="info-row" v-if="item.user_name">
                <span class="label">答题人</span>
                <span class="value">{{ item.user_name }}</span>
              </div>
              <div class="info-row" v-if="item.submit_time">
                <span class="label">提交时间</span>
                <span class="value">{{ formatTime(item.submit_time) }}</span>
              </div>
              <div class="info-row">
                <span class="label">创建时间</span>
                <span class="value">{{ formatTime(item.create_time) }}</span>
              </div>
            </div>

            <div class="card-footer">
              <button class="btn btn-default" @click="handleOpen(item)">
                {{ (item.status === 1 || item.status === 'submitted') ? '查看结果' : '开始答题' }}
              </button>
              <button class="btn btn-danger-outline" @click="handleDelete(item)">
                删除
              </button>
            </div>
          </div>
        </div>

        <div v-if="total > 0" class="pagination-wrap">
          <el-pagination
            background
            layout="total, prev,  next"
            :current-page="pageNo"
            :page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '@/utils/request'

const router = useRouter()

const list = ref([])
const total = ref(0)
const loading = ref(false)
const pageNo = ref(1)
const pageSize = ref(10)

const fetchList = async () => {
  try {
    loading.value = true
    const res = await request.get('/quiz/paper/data', {
      params: { pageNo: pageNo.value, pageSize: pageSize.value }
    })
    const data = res.data || res || {}
    if (Array.isArray(data)) {
      list.value = data
      total.value = data.length
    } else {
      list.value = data.rows || data.list || data.records || []
      total.value = data.total !== undefined ? data.total : list.value.length
    }
  } catch (e) {
    list.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

const handleSizeChange = (size) => {
  pageSize.value = size
  pageNo.value = 1
  fetchList()
}

const handleCurrentChange = (current) => {
  pageNo.value = current
  fetchList()
}

const handleCreate = async () => {
  try {
    loading.value = true
    await request.post('/quiz/paper/create', {})
    ElMessage.success('试卷生成成功')
    fetchList()
  } catch (e) {
    // error handled by interceptor
  } finally {
    loading.value = false
  }
}

const handleDelete = async (item) => {
  try {
    await ElMessageBox.confirm(`确定要删除这份试卷吗？`, '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    })
    await request.post(`/quiz/paper/delete`, { id: item.id })
    ElMessage.success('删除成功')
    fetchList()
  } catch (e) {
    if (e !== 'cancel') {
      // handled by interceptor
    }
  }
}

const handleOpen = (item) => {
  router.push({ path: `/quiz/paper/${item.id}` })
}

const handleTrain = () => {
  router.push('/quiz/train')
}

const formatTime = (t) => {
  if (!t) return '-'
  if (typeof t === 'string') return t.replace('T', ' ').slice(0, 19)
  if (typeof t === 'number') {
    const d = new Date(t)
    return d.toLocaleString('zh-CN').replace(/\//g, '-')
  }
  return '-'
}

const getStatusClass = (status) => {
  if (status === 1 || status === 'submitted' || status === 'done') return 'status-done'
  return 'status-pending'
}

const getStatusText = (status) => {
  if (status === 1 || status === 'submitted' || status === 'done') return '已完成'
  return '待完成'
}

const isPass = (item) => {
  if (item.user_total_score === undefined || item.user_total_score === null) return false
  const pass = item.total_score ? Math.round(item.total_score * 0.6) : 60
  return item.user_total_score >= pass
}

onMounted(() => {
  fetchList()
})
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  background:
    radial-gradient(ellipse at top left, rgba(99, 102, 241, 0.08), transparent 50%),
    radial-gradient(ellipse at bottom right, rgba(236, 72, 153, 0.06), transparent 50%),
    linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.paper-list {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px 40px;
}

.hero-banner {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  margin: 24px 0 28px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%);
  padding: 32px 36px;
  box-shadow:
    0 10px 40px -10px rgba(99, 102, 241, 0.4),
    0 2px 6px rgba(0, 0, 0, 0.05);
}

.hero-bg {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.15) 0%, transparent 40%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 35%),
    radial-gradient(circle at 60% 60%, rgba(255, 255, 255, 0.08) 0%, transparent 30%);
  pointer-events: none;
}

.hero-content {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.hero-text {
  color: #fff;
}

.page-title {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.5px;
}

.page-desc {
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.stat-pill {
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 4px 18px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.stat-pill .stat-num {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
}

.stat-pill .stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
}

.btn-icon {
  width: 16px;
  height: 16px;
}

.btn-primary {
  background: #fff;
  color: #6366f1;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-train {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
}

.btn-train:hover {
  background: rgba(255, 255, 255, 0.35);
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.btn-default {
  background: #fff;
  color: #374151;
  border: 1px solid #e5e7eb;
  padding: 7px 16px;
  font-size: 13px;
}

.btn-default:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

.btn-danger-outline {
  background: #fff;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 7px 16px;
  font-size: 13px;
}

.btn-danger-outline:hover {
  background: #fef2f2;
  border-color: #dc2626;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(99, 102, 241, 0.25);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.empty-icon {
  font-size: 56px;
  margin-bottom: 12px;
}

.empty-text {
  color: #9ca3af;
  font-size: 14px;
}

.paper-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 20px;
}

.paper-card {
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  padding: 22px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  position: relative;
  overflow: hidden;
}

.paper-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, transparent);
  transition: background 0.3s;
}

.paper-card:hover {
  transform: translateY(-4px);
  box-shadow:
    0 12px 32px -8px rgba(99, 102, 241, 0.15),
    0 4px 12px -4px rgba(0, 0, 0, 0.08);
  border-color: #e0e7ff;
}

.paper-card.submitted::before {
  background: linear-gradient(90deg, #10b981, #34d399);
}

.paper-card:not(.submitted):hover::before {
  background: linear-gradient(90deg, #6366f1, #ec4899);
}

.card-top {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.paper-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #eef2ff, #ede9fe);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.paper-info {
  flex: 1;
  min-width: 0;
}

.paper-title {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-meta {
  font-size: 12px;
  color: #9ca3af;
}

.status-tag {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 99px;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 500;
}

.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.status-done {
  background: #d1fae5;
  color: #065f46;
}

.card-body {
  flex: 1;
  border-top: 1px dashed #eef0f4;
  padding-top: 14px;
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  margin-bottom: 8px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row .label {
  color: #9ca3af;
}

.info-row .value {
  color: #374151;
  font-weight: 500;
}

.info-row .value.score.pass {
  color: #059669;
}

.info-row .value.score.fail {
  color: #dc2626;
}

.card-footer {
  display: flex;
  gap: 8px;
  padding-top: 14px;
  border-top: 1px solid #f3f4f6;
}

.pagination-wrap {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

@media (max-width: 768px) {
  .hero-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-banner {
    padding: 24px;
  }
}

@media (max-width: 600px) {
  .paper-list {
    padding: 0 12px 24px;
  }

  .paper-grid {
    grid-template-columns: 1fr;
  }

  .hero-banner {
    margin: 16px 0 20px;
    border-radius: 16px;
  }

  .page-title {
    font-size: 22px;
  }

  .hero-actions {
    width: 100%;
    justify-content: space-between;
  }
}
</style>